import React, {useState, useEffect, useCallback, useMemo, memo} from "react";
import {inject, observer} from "mobx-react";
import {useTranslation} from "react-i18next";
import {Table, Progress} from "antd";
//local
import {
  IconDocumentDalux,
  IconFile,
  IconFolder,
  IconArrowIn,
  IconArrowOut,
} from "components/icons";
import {Modal} from "components/Modal";
import {Info} from "components/Info";
import {ButtonTransparent} from "components/buttons/ButtonTransparent";
import {Preloader} from "components/Preloader";
import {daluxFolderType} from "constants/buyer.consts";
const isFolder = (entity) => !!entity?.folderName;
const isFolderWithFiles = (folder) => !!folder.children?.some((item) => !!item.fileId);

const EntityIcon = ({row}) => (
  <div className="df-row-center">
    {isFolder(row) ? (
      <IconFolder width="12" height="12" addClass="ml-1 mr-1" />
    ) : (
      <IconFile width="12" height="12" addClass="ml-1 mr-1" />
    )}
    <span>{row.name}</span>
  </div>
);

const AddDaluxLinkModal = inject("store")(
  observer(({store: {buyer}, revisionId, addLink}) => {
    const {currentRequest, currentProject} = buyer;
    const [loading, setLoading] = useState(true);
    const [daluxProjectName, setDaluxProjectName] = useState("");
    const [loadingFilesInfo, setLoadingFilesInfo] = useState({
      percent: 5,
      time: 2,
    });
    const [foldersFilesList, setFoldersFilesList] = useState([]);
    const [selectedLinks, setSelectedLinks] = useState([]);
    const {t} = useTranslation();

    const expandedFolders = useMemo(() => new Map(), []);
    const revisionLinks = useMemo(
      () => currentRequest?.documents?.find((item) => item.id === revisionId).links ?? [],
      [currentRequest, revisionId]
    );
    const projectId = useMemo(
      () => currentProject?.id ?? currentRequest?.Project?.id,
      [currentProject, currentRequest]
    );
    const daluxProjectId = useMemo(
      () => currentProject?.daluxProjectId ?? currentRequest?.Project?.daluxProjectId,
      [currentProject, currentRequest]
    );

    const columns = [
      {
        title: t("NAME"),
        dataIndex: "name",
        key: "name",
        render: (_, row) => <EntityIcon row={row} />,
      },
      {
        title: t("ADD_MODAL_DALUX_TABLE_VERSION_COLUMN"),
        dataIndex: "version",
        key: "version",
        width: "132px",
      },
      {
        title: t("ADD_MODAL_DALUX_TABLE_CHANGED_COLUMN"),
        dataIndex: "lastModified",
        key: "lastModified",
        width: "132px",
      },
    ];

    const customExpandIcon = ({expanded, record, onExpand}) => {
      if (!record.children?.length) return <div className="wdth-18"></div>;
      return expanded ? (
        <IconArrowOut
          addClass="mr-1 cpointer"
          onClick={(e) => {
            expandedFolders.delete(record.folderId);
            onExpand(record, e);
          }}
        />
      ) : (
        <IconArrowIn
          addClass="mr-1 cpointer"
          onClick={(e) => {
            isFolderWithFiles(record) &&
              expandedFolders.set(record.folderId, {
                name: record.name,
                type: record.type,
              });
            onExpand(record, e);
          }}
        />
      );
    };

    const SuffixTitleIcon = memo(() => {
      const keyInfo = "ADD_MODAL_DALUX_TITLE_LOAD_FILES_INFO";
      const keyTooltip = "ADD_MODAL_DALUX_TITLE_LOAD_FILES_TOOLTIP";
      const isLoaded = loadingFilesInfo.percent === 100;
      const infoText = isLoaded
        ? t(`${keyInfo}_SUCCESS`)
        : t(`${keyInfo}_PROGRESS`).format(loadingFilesInfo.time);
      const tooltipText = isLoaded
        ? t(`${keyTooltip}_SUCCESS`)
        : t(`${keyTooltip}_PROGRESS`);

      return (
        <React.Fragment>
          <Info
            text={infoText}
            tooltip={tooltipText}
            addTextClass="fz-14 __bold"
            addClass="ml-4"
          />
          <Progress
            percent={loadingFilesInfo.percent}
            showInfo={isLoaded}
            status={isLoaded ? "success" : "active"}
            style={{marginBottom: "2px"}}
            className={`wdth-${isLoaded ? "36" : "300"} ml-2`}
          />
        </React.Fragment>
      );
    });

    const createTreeList = useCallback((list, parentId) => {
      if (!list.length) return;
      const childrenList = list.filter((item) => item.parentFolderId === parentId);
      const innerList = list.filter((item) => item.parentFolderId !== parentId);
      return childrenList.map((item) => ({
        ...item,
        children: [...item.children, createTreeList(innerList, item.folderId)].flat()
          .length
          ? [...item.children, createTreeList(innerList, item.folderId)].flat()
          : null,
      }));
    }, []);

    const fillDaluxFilesFolders = (folders, data) => {
      const files = new Map();
      data?.forEach((file) => {
        const data = {
          ...file.data,
          name: file?.data?.fileName,
          revisionId: file?.data?.fileRevisionId,
          key: file?.data?.fileId,
        };
        files.set(
          file?.data?.folderId,
          files.has(file?.data?.folderId)
            ? [...files.get(file?.data?.folderId), data]
            : [data]
        );
      });
      const filesFolders = folders?.slice(1).map((folder) => ({
        ...folder,
        children: files.get(folder.folderId) ?? [],
      }));
      return filesFolders;
    };

    const getDaluxFiles = useCallback(
      async (daluxProjectId, fileAreaId, foldersFilesList, folders, files, bookmark) => {
        const filesData = await buyer.getDaluxProjectFileAreaFiles(
          daluxProjectId,
          fileAreaId,
          bookmark
        );
        const fileItems = [...(files ?? []), ...filesData.items];
        const filesFolders = fillDaluxFilesFolders(folders, fileItems);
        const finalList = createTreeList(filesFolders, folders?.[0].folderId);

        const updatedList = foldersFilesList.map((item) =>
          item.folderId === folders?.[0].folderId ? {...item, children: finalList} : item
        );
        setFoldersFilesList(updatedList);

        const nextBookmark = filesData.metadata?.bookmark;
        const totalRemainingItems = filesData.metadata?.totalRemainingItems
          ? filesData.metadata?.totalRemainingItems - filesData.items.length
          : 0;
        const percent = Math.round((fileItems.length / totalRemainingItems) * 100);
        const time = Math.round(totalRemainingItems / 2000);
        const isNextRequestNeeded = !!totalRemainingItems && !!nextBookmark;
        setLoadingFilesInfo({percent: isNextRequestNeeded ? percent : 100, time});
        if (isNextRequestNeeded)
          getDaluxFiles(
            daluxProjectId,
            fileAreaId,
            foldersFilesList,
            folders,
            fileItems,
            nextBookmark
          );
      },
      [buyer, createTreeList]
    );

    const getDaluxFolders = useCallback(
      async (daluxProjectId, fileAreaId, rootFolderName) => {
        const dataRequests = [
          buyer.getDaluxProjectFileAreaFolders(daluxProjectId, fileAreaId),
          buyer.getDaluxProjectFileAreaVersionSets(daluxProjectId, fileAreaId, projectId),
        ];

        const data = (await Promise.allSettled(dataRequests))
          .filter((item) => item.status === "fulfilled")
          .map((item) => item.value);
        const [foldersData, versionSetsData] = data;

        const folders = foldersData?.map((folder) => ({
          ...folder,
          name: folder.folderName,
          type: daluxFolderType.FOLDER,
          key: `${fileAreaId}_${folder.folderId}`,
        }));

        const filesFolders = fillDaluxFilesFolders(folders, []);
        const filesVersionSets =
          versionSetsData?.map((versionSet) => ({
            ...versionSet.data,
            folderId: versionSet.data.versionSetId,
            folderName: versionSet.data.name,
            type: daluxFolderType.VERSION_SET,
            key: `${fileAreaId}_${versionSet.data.versionSetId}`,
            children: versionSet.data.children.map((file) => ({
              ...file.data,
              name: file.data.fileName,
              revisionId: file.data.fileRevisionId,
              folderId: versionSet.data.versionSetId,
              key: `${file.data.fileId}_${versionSet.data.versionSetId}`,
            })),
          })) ?? [];

        const finalList = createTreeList(filesFolders, folders?.[0].folderId);
        const rootFolder = {
          ...(folders?.[0] ?? {}),
          key: `${fileAreaId}_${folders?.[0].folderId}`,
          name: rootFolderName,
          folderName: rootFolderName,
          children: finalList,
        };
        const versionSetRootFolderName = `${t(
          "ADD_MODAL_DALUX_TABLE_VERSION_SETS_ROOT_FOLDER"
        )} (${rootFolderName})`;
        const versionSetRootFolder = {
          folderId: "23428071861",
          key: `${fileAreaId}_23428071861`,
          name: versionSetRootFolderName,
          folderName: versionSetRootFolderName,
          type: daluxFolderType.VERSION_SET,
          children: filesVersionSets,
        };
        return {tree: [rootFolder, versionSetRootFolder], folders};
      },
      [buyer, createTreeList, projectId, t]
    );

    const getDaluxInfo = useCallback(async () => {
      const data = await buyer.getDaluxProject(daluxProjectId);
      setDaluxProjectName(data.projectName);
      const areas = await buyer.getDaluxProjectFileAreas(daluxProjectId);
      const fillFoldersFileList = areas.map((area) => {
        return new Promise(async (resolve, reject) => {
          try {
            const data = await getDaluxFolders(
              daluxProjectId,
              area.fileAreaId,
              area.fileAreaName
            );
            resolve(data);
          } catch (error) {
            reject(error);
          }
        });
      });

      const areasData = await Promise.all(fillFoldersFileList);
      const foldersFilesList = areasData.map((item) => item.tree).flat();
      setFoldersFilesList(foldersFilesList);
      areas.forEach((area, i) =>
        getDaluxFiles(
          daluxProjectId,
          area.fileAreaId,
          foldersFilesList,
          areasData[i].folders
        )
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buyer, daluxProjectId, getDaluxFolders]);

    const handleRowSelect = (rows) => {
      const rowsFolders = rows
        .filter(isFolder)
        .map((folder) => [folder.folderId, {name: folder.name, type: folder.type}]);
      const selectedFilesFolders = new Map([...rowsFolders, ...expandedFolders]);
      const reorganizedRows = rows
        .filter((row) => !isFolder(row))
        .map((row) => {
          const isVersionSet =
            selectedFilesFolders.get(row.folderId)?.type === daluxFolderType.VERSION_SET;
          const revisionId = isVersionSet ? row.revisionId : "null";
          const folderName = selectedFilesFolders.get(row.folderId)?.name ?? "null";
          return `daluxFile/${row.fileName}/${daluxProjectId}/${row.fileAreaId}/${row.fileId}/${revisionId}/${row.folderId}/${folderName}`;
        });
      setSelectedLinks(reorganizedRows);
    };

    // rowSelection objects indicates the need for row selection
    const rowSelection = {
      getCheckboxProps: (record) => ({
        disabled: !(record.children || record.fileId), //disable folder checkbox without files
      }),
      onSelect: (record, selected, selectedRows) => handleRowSelect(selectedRows),
    };

    const handleSubmit = () => {
      const addedLinks = selectedLinks
        .filter((item) => !revisionLinks.map((link) => link.url).includes(item))
        .map((url) => ({url}));
      addLink(addedLinks);
      buyer.setAddDaluxLinkModalStatus(false);
    };

    useEffect(() => {
      getDaluxInfo().finally(() => setLoading(false));
    }, [getDaluxInfo]);

    return (
      !!daluxProjectName && (
        <Modal
          titleIcon={<IconDocumentDalux addClass="mr-4" />}
          titleText={daluxProjectName}
          suffixTitleIcon={<SuffixTitleIcon />}
          whiteTitle
          percentageWide="85%"
          noCenterContent
          noCloseButton
          noCloseOnEsc
          rounded
          onClose={() => buyer.setAddDaluxLinkModalStatus(false)}
        >
          <div className="add-dalux-link-modal__content">
            <div className="add-dalux-link-modal__file-list">
              {loading ? (
                <Preloader addClass="preloader250" />
              ) : (
                <Table
                  columns={columns}
                  rowSelection={{
                    ...rowSelection,
                    checkStrictly: false,
                    hideSelectAll: true,
                  }}
                  expandIcon={(props) => customExpandIcon(props)}
                  dataSource={foldersFilesList}
                  pagination={false}
                  rowClassName={(_, index) => (index % 2 === 1 ? "colored" : "")}
                  scroll={{y: "50vh"}}
                />
              )}
            </div>

            <div className="df-row-jce-center mt-4 ml-1">
              <Info
                text={t("ADD_MODAL_DALUX_INFO_BLOB")}
                addTextClass="fz-14"
                addClass="mra"
              />
              <ButtonTransparent
                name="CANCEL"
                color="red"
                medium
                onClick={() => buyer.setAddDaluxLinkModalStatus(false)}
                addClass="mr-4"
              />
              <ButtonTransparent
                name="ADD_SELECTED"
                medium
                nonTransparent
                onClick={handleSubmit}
                disabled={!selectedLinks.length}
              />
            </div>
          </div>
        </Modal>
      )
    );
  })
);

export default AddDaluxLinkModal;
